import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import type { usePinnedPages } from "@/vnext/app-shell/usePinnedPages";
import { useShellDismiss } from "@/vnext/app-shell/useShellDismiss";
import { cx } from "@/vnext/shared/cx";

/** F-205: the Pinned dropdown under its top bar button. */
export function ShellPinnedMenu({
  pins,
}: {
  pins: ReturnType<typeof usePinnedPages>;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  }, []);
  useShellDismiss({
    open,
    onClose: close,
    refs: useMemo(() => [buttonRef, menuRef], []),
  });

  return (
    <div className="vnext-shell-pinned">
      <button
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        className={cx("vnext-shell-tool", open && "is-open")}
        onClick={() => setOpen((current) => !current)}
        ref={buttonRef}
        type="button"
      >
        <PushPinOutlinedIcon aria-hidden="true" sx={{ fontSize: 14 }} />
        Pinned
      </button>
      {open ? (
        <div
          aria-label="Pinned pages"
          className="vnext-shell-pinned__menu"
          id={menuId}
          ref={menuRef}
          role="group"
        >
          <div className="vnext-shell-pinned__title">Pinned pages</div>
          {pins.pinned.length === 0 ? (
            <div className="vnext-shell-pinned__empty">Nothing pinned yet.</div>
          ) : (
            pins.pinned.map((page) => (
              <div className="vnext-shell-pinned__row" key={page.id}>
                <Link
                  className="vnext-shell-pinned__link"
                  onClick={() => setOpen(false)}
                  to={page.path}
                >
                  <span className="vnext-shell-pinned__name">{page.label}</span>
                  <span className="vnext-shell-pinned__section">
                    {page.sectionLabel}
                  </span>
                </Link>
                <button
                  aria-label={`Unpin ${page.label}`}
                  className="vnext-shell-pinned__unpin"
                  onClick={() => pins.unpin(page.id)}
                  type="button"
                >
                  <CloseOutlinedIcon aria-hidden="true" sx={{ fontSize: 13 }} />
                </button>
              </div>
            ))
          )}
          {pins.currentPage ? (
            <>
              <div aria-hidden="true" className="vnext-shell-pinned__divider" />
              <button
                className="vnext-shell-pinned__toggle"
                onClick={pins.toggleCurrent}
                type="button"
              >
                {pins.isCurrentPinned ? "Unpin this page" : "Pin this page"}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
